"use client";
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

export default function GalleryPage() {
  const [selectedImg, setSelectedImg] = useState(null);

  // High-quality data representing your core categories
  const galleryItems = [
    { id: 1, category: "Enterprise Servers", title: "Data Center Solutions", src: "https://ik.imagekit.io/igxrggga8/pcworldraipur.in/Banners%20frontsite/enterprise-server-e1605913545212.jpg", caption: "High-performance server racks and infrastructure for enterprise-level data management." },
    { id: 2, category: "CCTV Surveillance", title: "Security Infrastructure", src: "https://images.unsplash.com/photo-1557597774-9d273605dfa9?q=80&w=800", caption: "Advanced surveillance monitoring systems designed for industrial and corporate security." },
    { id: 3, category: "Cybersecurity", title: "Network Protection", src: "https://images.unsplash.com/photo-1550751827-4bd374c3f58b?q=80&w=800", caption: "Multi-layered firewall deployment and cybersecurity protocols to safeguard corporate data." },
    { id: 4, category: "Desktops", title: "Corporate Workstations", src: "https://images.unsplash.com/photo-1593640408182-31c70c8268f5?q=80&w=800", caption: "Powerful desktop configurations optimized for high-intensity business productivity." },
    { id: 5, category: "Networking", title: "Campus Connectivity", src: "https://images.unsplash.com/photo-1544197150-b99a580bb7a8?q=80&w=800", caption: "Seamless wired and wireless networking solutions for educational and corporate campuses." },
    { id: 6, category: "Interactive Panels", title: "Smart Learning Tech", src: "https://images.unsplash.com/photo-1509062522246-3755977927d7?q=80&w=800", caption: "Modern interactive panels for immersive digital learning environments and boardrooms." },
    { id: 7, category: "LED Walls", title: "Digital Displays", src: "https://images.unsplash.com/photo-1542744173-8e7e53415bb0?q=80&w=800", caption: "Large-scale LED video walls for high-impact brand communication and events." },
    { id: 8, category: "Cloud Services", title: "Hybrid Cloud Setup", src: "https://images.unsplash.com/photo-1451187580459-43490279c0fa?q=80&w=800", caption: "Scalable cloud infrastructure management for modern, digital-first business operations." },
    { id: 9, category: "Power Solutions", title: "Backup Power Systems", src: "https://images.unsplash.com/photo-1581092160607-ee22621dd758?q=80&w=800", caption: "Industrial grade DG sets and Online UPS systems ensuring zero business downtime." },
    { id: 10, category: "Laptops", title: "Business Mobility", src: "https://images.unsplash.com/photo-1496181133206-80ce9b88a853?q=80&w=800", caption: "Premium business laptops for professionals who require power and portability." },
    { id: 11, category: "Video Conferencing", title: "Collaboration Rooms", src: "https://images.unsplash.com/photo-1517048676732-d65bc937f952?q=80&w=800", caption: "Advanced VC setups for crystal clear remote collaboration and virtual meetings." },
    { id: 12, category: "Digital Learning", title: "E-Learning Labs", src: "https://images.unsplash.com/photo-1571260899304-425eee4c7efc?q=80&w=800", caption: "Complete computer lab setups with centralized management for schools and colleges." },
  ];

  return (
    <div className="min-h-screen bg-white pb-20 selection:bg-blue-600 selection:text-white">
      
      {/* 1. HEADER */}
      <section className="pt-24 pb-16 px-6 bg-slate-900 text-white relative">
        <div className="max-w-7xl mx-auto relative z-10 text-center lg:text-left">
          <h1 className="text-4xl lg:text-7xl font-black tracking-tighter mb-4 uppercase italic">Products & Services Gallery</h1>
          <p className="text-blue-400 font-bold uppercase tracking-[0.3em] text-[10px]">Visualizing Excellence in IT Infrastructure</p>
        </div>
      </section>

      {/* 2. GALLERY GRID (3 Desktop / 2 Mobile) */}
      <section className="py-16 px-4 lg:px-6">
        <div className="max-w-7xl mx-auto grid grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12">
          {galleryItems.map((item) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="group cursor-pointer"
              onClick={() => setSelectedImg(item)}
            >
              {/* Image Container */}
              <div className="aspect-square rounded-[2rem] lg:rounded-[3rem] overflow-hidden bg-slate-100 border border-slate-100 shadow-sm transition-all duration-500 group-hover:shadow-2xl group-hover:-translate-y-2">
                <img 
                  src={item.src} 
                  alt={item.title} 
                  className="w-full h-full object-cover transition duration-700"
                />
              </div>
              
              {/* Visible Info Area (No Hover Required) */}
              <div className="mt-6 px-2">
                <span className="text-blue-600 font-black uppercase tracking-widest text-[9px] block mb-1">
                  {item.category}
                </span>
                <h3 className="text-slate-900 font-black text-sm lg:text-xl uppercase tracking-tighter group-hover:text-blue-600 transition-colors">
                  {item.title}
                </h3>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* 3. LIGHTBOX MODAL */}
      <AnimatePresence>
        {selectedImg && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 lg:p-12 bg-slate-950/98 backdrop-blur-xl"
            onClick={() => setSelectedImg(null)}
          >
            <motion.div 
              initial={{ scale: 0.9, y: 30 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 30 }}
              className="relative max-w-6xl w-full bg-white rounded-[2.5rem] lg:rounded-[4rem] overflow-hidden shadow-2xl flex flex-col lg:flex-row"
              onClick={(e) => e.stopPropagation()}
            >
              <button 
                className="absolute top-6 right-6 lg:top-8 lg:right-8 text-slate-400 hover:text-blue-600 transition text-2xl z-10"
                onClick={() => setSelectedImg(null)}
              >
                ✕
              </button>

              {/* Image Side */}
              <div className="w-full lg:w-2/3 aspect-video lg:aspect-square overflow-hidden bg-black">
                <img src={selectedImg.src} className="w-full h-full object-cover" alt={selectedImg.title} />
              </div>

              {/* Detail Side */}
              <div className="w-full lg:w-1/3 p-8 lg:p-16 flex flex-col justify-center bg-white">
                <span className="text-blue-600 font-black uppercase tracking-[0.3em] text-[10px] mb-4">
                  {selectedImg.category}
                </span>
                <h2 className="text-3xl lg:text-5xl font-black text-slate-900 mb-6 leading-none tracking-tighter uppercase">
                  {selectedImg.title}
                </h2>
                <p className="text-slate-500 text-sm lg:text-base font-medium leading-loose mb-10 border-l-4 border-slate-100 pl-6">
                  {selectedImg.caption}
                </p>
                <button 
                  onClick={() => setSelectedImg(null)}
                  className="bg-slate-900 text-white py-4 lg:py-5 rounded-2xl font-black uppercase text-xs tracking-widest hover:bg-blue-600 transition-all shadow-xl active:scale-95"
                >
                  Close Preview
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

       

    </div>
  );
}